// Last step: confirm before anything is created.
//
// Deploying pulls a multi-GB image and creates a container, so the operator sees
// exactly what is about to happen — including the derived names they never typed —
// with a way back to change any of it.
//
// The remote branch shows the compose file itself, rendered by the same generator
// the server writes over SSH. Not an illustration of it: a review that showed an
// approximation would be worse than no review, because it would be believed.

import {
	REMOTE_COMPOSE_FILE_NAME,
	remoteWorkerDir,
} from "@lib/remote-bootstrap.ts";
import { renderRemoteWorkerCompose } from "@lib/remote-compose.ts";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import type { DeployWorkerValue } from "../schema";
import { toComposeInfo, useProvisionInfo } from "../use-provision-info";
import type { WorkerImageVersions } from "../use-worker-images";

export function ReviewStep({
	values,
	images,
	keyLabel,
	onEdit,
}: {
	values: DeployWorkerValue;
	images: WorkerImageVersions | undefined;
	/** Which key opens the machine, phrased by the wizard hook. */
	keyLabel: string;
	onEdit: (stepId: string) => void;
}) {
	const resolved =
		values.imageTag === "latest" && images?.latest
			? `latest (${images.latest})`
			: values.imageTag;
	const remote = values.target === "remote";

	return (
		<div className="grid gap-3">
			<Row
				label="Runs on"
				value={
					remote
						? `${values.sshUser}@${values.sshHost}:${values.sshPort}`
						: "This machine"
				}
				onEdit={() => onEdit("target")}
			/>
			<Row
				label="Name"
				value={values.name}
				onEdit={() => onEdit("configure")}
			/>
			<Row
				label="Version"
				value={resolved}
				onEdit={() => onEdit("configure")}
			/>
			<Row label="Container" value={`veil-worker-${values.name}`} />
			{remote ? (
				<>
					<Row label="Directory" value={remoteWorkerDir(values.name)} />
					<Row
						label="SSH key"
						value={keyLabel}
						onEdit={() => onEdit("connection")}
					/>
					<Row
						label="Options"
						value={remoteOptions(values)}
						onEdit={() => onEdit("connection")}
					/>
					<RemoteCompose values={values} />
					<p className="mt-1 text-xs text-muted-foreground">
						Deploying writes those two files, pulls the image with that
						machine's own Docker and waits for the worker to answer. Then this
						manager probes it from here before adding it to the pool — that
						probe is what catches a firewall in front of the host, which nothing
						on the machine itself can see.
					</p>
				</>
			) : (
				<>
					<Row label="Data volume" value={`veil-install-${values.name}`} />
					<p className="mt-1 text-xs text-muted-foreground">
						Deploying pulls the worker image, starts the container and adds it
						to the load-balancer pool. The browser builds it needs are
						downloaded on first boot, so it may take a few minutes to report
						healthy.
					</p>
				</>
			)}
		</div>
	);
}

/** The advanced toggles, as a sentence, since most deploys leave them alone. */
function remoteOptions(values: DeployWorkerValue): string {
	const on = [
		values.installDocker ? "install Docker" : null,
		values.restrictFirewall ? "restrict firewall" : null,
		values.autoUpdate ? "one-click updates" : null,
	].filter((v): v is string => v !== null);
	return on.length > 0 ? on.join(", ") : "defaults";
}

/**
 * The file that will be written to the remote host, from the shared generator.
 *
 * Rendered in the SSH path's mode, so secrets appear as the `${VEIL_KEY}` /
 * `${AGENT_TOKEN}` interpolations that the sibling `.env` resolves — which is
 * both what actually gets written and the reason this is safe to put on screen.
 */
function RemoteCompose({ values }: { values: DeployWorkerValue }) {
	const info = useProvisionInfo();

	if (!info.data) {
		return (
			<p className="text-xs text-muted-foreground">
				Loading this cluster's registry settings…
			</p>
		);
	}

	if (info.data.remoteReleaseOrigin === null) {
		return (
			<p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
				<Badge variant="warning" className="mr-2">
					Action needed
				</Badge>
				{info.data.remoteReleaseOriginProblem ??
					"No publicly reachable release origin is configured, so a worker on another machine cannot fetch its browser bundles."}
			</p>
		);
	}

	const compose = renderRemoteWorkerCompose({
		info: toComposeInfo(info.data, values.imageTag),
		hostname: values.name,
		autoUpdate: values.autoUpdate,
		secretMode: "env-file",
		composeFileName: REMOTE_COMPOSE_FILE_NAME,
	});

	return (
		<div>
			<p className="mb-1 text-xs text-muted-foreground">
				<code>
					{remoteWorkerDir(values.name)}/{REMOTE_COMPOSE_FILE_NAME}
				</code>{" "}
				— the org key and agent token go in a <code>.env</code> beside it, mode
				0600, so they are not readable out of this file.
			</p>
			{/* Scrolls sideways only: a compose file's long lines are worth
			    keeping intact, its length is the page's problem. */}
			<pre className="overflow-x-auto rounded-md bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
				{compose}
			</pre>
		</div>
	);
}

function Row({
	label,
	value,
	onEdit,
}: {
	label: string;
	value: string;
	onEdit?: () => void;
}) {
	return (
		<div className="flex items-center justify-between gap-3 border-b border-border/60 pb-2 text-sm last:border-0">
			<span className="text-muted-foreground">{label}</span>
			<span className="flex min-w-0 items-center gap-2">
				<span className="truncate font-mono text-xs">{value}</span>
				{onEdit ? (
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						onClick={onEdit}
						aria-label={`Change ${label.toLowerCase()}`}
						title={`Change ${label.toLowerCase()}`}
					>
						<Pencil className="size-3" />
					</Button>
				) : null}
			</span>
		</div>
	);
}
